import { makeAutoObservable, runInAction } from 'mobx';
import {
  ChatMessage,
  ChatModelMode,
  ChatStreamChunk,
  OnBoardingItem,
  PatchNotes as PatchNotesState,
  Version
} from '../entity';
import { PatchNotesEntry } from '../patchNotes';
import { requireTokenSubject } from '../../utils/auth';
import { applyBaseStore } from './BaseStore';
import type { BaseStore } from './BaseStore';
import { ChatStreamingService } from '../services/ChatStreamingService';
import { PatchNotesStateService } from '../services/PatchNotesStateService';
import { PermissionsService } from '../services/PermissionsService';

export interface GeneralStore extends BaseStore {}

export class GeneralStore {
  private readonly chatStreamingService = new ChatStreamingService();

  private readonly patchNotesStateService = new PatchNotesStateService();

  private readonly permissionsService = new PermissionsService();

  chatMeta = { prompt: '' };

  webAccessEnabled = true;

  chatModelMode: ChatModelMode = 'fast';

  version: Version;

  permissions: string[] = [];

  permissionsLoaded = false;

  permissionsError = '';

  messages: ChatMessage[] = [];

  chatStatus = '';

  chats = [];

  onboardingItems: OnBoardingItem[] = [];

  activeChatId = undefined;

  activeChatName = 'Neuer Chat';

  activeChatSummary = '';

  threadId: string = '';

  activeAgentId = 'x1F';

  patchNotes: PatchNotesState = undefined;

  patchNotesDialogOpen = false;

  patchNotesLoading = false;

  patchNotesNeedsAcknowledgement = false;

  selectedPatchNotesVersion: string = undefined;

  patchNotesList: PatchNotesEntry[] = [];

  passwordChangeResult = undefined;

  private versionFetchPromise?: Promise<void> = undefined;

  private lastVersionFetchAt = 0;

  private readonly versionRefreshIntervalMs = 5 * 60 * 1000;

  private chatAutoSavePromise?: Promise<unknown> = undefined;

  private chatAutoSavePending = false;

  private chatRequestInFlight = false;

  private chatRequestGeneration = 0;

  constructor() {
    applyBaseStore(this);
    makeAutoObservable(this, {
      chatStreamingService: false,
      patchNotesStateService: false,
      permissionsService: false,
      versionFetchPromise: false,
      lastVersionFetchAt: false,
      versionRefreshIntervalMs: false,
      chatAutoSavePromise: false,
      chatAutoSavePending: false
    } as any);
  }

  hasPermission(permission: string): boolean {
    const normalizedPermission = String(permission || '')
      .trim()
      .toUpperCase();
    return this.permissions.some(
      (availablePermission) =>
        String(availablePermission || '')
          .trim()
          .toUpperCase() === normalizedPermission
    );
  }

  async getPermissions(force = false) {
    await this.fetchPermissions(force);
  }

  async fetchVersion() {
    await this.getVersion();
  }

  async trackFeatureView(view: 'sesam' | 'company' | 'bid') {
    try {
      await this.api.fetch({
        endpoint: `${this.helper.getBaseUrl}/api/usage/views`,
        method: 'POST',
        body: JSON.stringify({ view })
      });
    } catch {
      // Nutzungsstatistiken sollen den Aufruf einer Ansicht nie blockieren.
    }
  }

  async fetchPatchNotes() {
    if (this.patchNotesLoading) {
      return;
    }

    runInAction(() => {
      this.patchNotesLoading = true;
      this.patchNotesList = this.patchNotesStateService.getList();
    });

    try {
      const patchNotesState = await this.patchNotesStateService.loadCurrent();

      if (!patchNotesState) {
        return;
      }

      runInAction(() => {
        this.patchNotes = patchNotesState.patchNotes;
        this.selectedPatchNotesVersion = patchNotesState.selectedVersion;
        this.patchNotesNeedsAcknowledgement =
          patchNotesState.needsAcknowledgement;
        this.patchNotesDialogOpen = patchNotesState.shouldOpenDialog;
      });
    } finally {
      runInAction(() => {
        this.patchNotesLoading = false;
      });
    }
  }

  async openPatchNotes() {
    if (!this.patchNotes) {
      const patchNotesState =
        await this.patchNotesStateService.loadCurrentVersion(
          this.patchNotesNeedsAcknowledgement
        );

      if (patchNotesState) {
        runInAction(() => {
          this.patchNotes = patchNotesState.patchNotes;
          this.selectedPatchNotesVersion = patchNotesState.selectedVersion;
        });
      }
    }

    runInAction(() => {
      this.patchNotesDialogOpen = Boolean(this.patchNotes);
    });
  }

  async selectPatchNotesVersion(version: string) {
    const patchNotesState = await this.patchNotesStateService.loadVersion(
      version,
      this.patchNotesNeedsAcknowledgement
    );

    if (!patchNotesState) {
      return;
    }

    runInAction(() => {
      this.patchNotes = patchNotesState.patchNotes;
      this.selectedPatchNotesVersion = patchNotesState.selectedVersion;
    });
  }

  acknowledgePatchNotes() {
    const needsAcknowledgement = this.patchNotesNeedsAcknowledgement;

    runInAction(() => {
      this.patchNotesDialogOpen = false;
      this.patchNotesNeedsAcknowledgement = false;
      if (this.patchNotes) {
        this.patchNotes.should_show = false;
      }
    });

    this.patchNotesStateService.acknowledgeIfNeeded(needsAcknowledgement);
  }

  setActiveAgentId(agentId?: string) {
    this.activeAgentId = this.resolveChatAgentId(agentId);
  }

  setWebAccessEnabled(_enabled: boolean) {
    // Der zentrale Chat arbeitet immer mit Webzugriff.
    this.webAccessEnabled = true;
  }

  setChatModelMode(mode: ChatModelMode) {
    if (this.chatModelMode === mode) {
      return;
    }

    this.chatModelMode = mode;
    if (this.threadId?.startsWith('local-') && this.messages.length === 0) {
      this.threadId = '';
      this.activeChatSummary = '';
    }
    void this.warmUpChat();
  }

  get hasActiveChatRequest() {
    return (
      this.chatRequestInFlight || this.chatStreamingService.isActiveRequest()
    );
  }

  cancelActiveChatRequest() {
    this.chatRequestGeneration += 1;
    this.chatStreamingService.cancelActiveRequest();
  }

  async fetchPermissions(force = false) {
    if (
      !force &&
      this.permissionsLoaded &&
      this.permissionsService.isLoadedForCurrentToken()
    ) {
      return;
    }

    runInAction(() => {
      this.permissionsError = '';
      if (force) {
        this.permissionsLoaded = false;
      }
    });

    try {
      const permissions = await this.permissionsService.load(
        this.api,
        this.endpointPermissions
      );
      runInAction(() => {
        this.permissions = permissions;
        this.permissionsLoaded = true;
        this.permissionsError = '';
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error && 'message' in error
          ? String((error as { message?: unknown }).message || '')
          : 'Berechtigungen konnten nicht geladen werden.';

      runInAction(() => {
        this.permissions = [];
        this.permissionsLoaded = false;
        this.permissionsError =
          message || 'Berechtigungen konnten nicht geladen werden.';
      });
    }
  }

  async getVersion(force = false) {
    const now = Date.now();

    if (
      !force &&
      this.version &&
      now - this.lastVersionFetchAt < this.versionRefreshIntervalMs
    ) {
      return;
    }

    if (this.versionFetchPromise) {
      return this.versionFetchPromise;
    }

    this.versionFetchPromise = (async () => {
      const response = await this.api.fetch({
        endpoint: `${this.helper.getBaseUrl + '/api/version'}`,
        method: 'GET'
      });

      runInAction(() => {
        this.version = response;
        this.lastVersionFetchAt = Date.now();
      });
    })();

    try {
      return await this.versionFetchPromise;
    } finally {
      this.versionFetchPromise = undefined;
    }
  }

  async changePassword(newPassword: string, oldPassword: string) {
    const username = requireTokenSubject();

    if (!username) {
      return;
    }

    const response = await this.api.fetch({
      endpoint: `${this.endpointChangePassword}`,
      method: 'POST',
      body: JSON.stringify({
        username,
        password: await this.helper.hashSHA256(oldPassword),
        new_password: await this.helper.hashSHA256(newPassword)
      })
    });

    if (this.hasNoResponse(response)) {
      runInAction(() => {
        this.passwordChangeResult = null;
      });
      return;
    }

    runInAction(() => {
      this.passwordChangeResult = response;
    });
  }

  async changePS(oldPassword: string, newPassword: string) {
    await this.changePassword(newPassword, oldPassword);
  }

  async chat(userMessage: ChatMessage, documentFiles: File[] = []) {
    if (this.hasActiveChatRequest) {
      return;
    }
    this.chatRequestInFlight = true;
    const requestGeneration = ++this.chatRequestGeneration;
    try {
      runInAction(() => {
        this.chatStatus = 'Anfrage wird verarbeitet.';
        this.messages.push(userMessage);
        this.messages.push({
          role: 'assistant',
          content: '',
          thread_id: this.threadId
        });
      });

      try {
        if (!this.threadId) {
          await this.newChat();
        }
        if (requestGeneration !== this.chatRequestGeneration) {
          const error = new Error('Die Chat-Anfrage wurde abgebrochen.');
          error.name = 'AbortError';
          throw error;
        }

        const responseStream = this.chatStreamingService.streamChat({
          endpointChat: this.endpointChat,
          endpointAgent: this.endpointAgent,
          threadId: this.threadId,
          agentId: this.resolveChatAgentId(),
          messages: this.messages,
          conversationSummary: this.activeChatSummary,
          webAccessEnabled: this.webAccessEnabled,
          modelMode: this.chatModelMode,
          documentFiles,
          onThreadId: (threadId) => {
            this.threadId = threadId;
          }
        });
        await this.consumeChatStream(responseStream);
      } catch (error) {
        if (this.isAbortError(error)) {
          runInAction(() => {
            this.markLastAssistantMessageCanceled();
            this.chatStatus = 'Antwort abgebrochen.';
          });
        } else {
          runInAction(() => {
            this.removeFailedChatRequest(userMessage);
            this.chatStatus = '';
          });
          throw error;
        }
      }

      runInAction(() => {
        this.chatStatus = '';
      });
      await this.autoSaveCurrentChatSafely();
    } finally {
      runInAction(() => {
        this.chatRequestInFlight = false;
      });
    }
  }

  async agentChat(
    userMessage: ChatMessage,
    agentId: string,
    webAccessEnabled = true
  ) {
    if (this.hasActiveChatRequest) {
      return;
    }
    this.chatRequestInFlight = true;
    const requestGeneration = ++this.chatRequestGeneration;
    try {
      runInAction(() => {
        this.chatStatus = 'Anfrage wird verarbeitet.';
        this.messages.push(userMessage);
        this.messages.push({
          role: 'assistant',
          content: '',
          thread_id: this.threadId
        });
      });

      try {
        if (!agentId) {
          throw new Error('Kein Scope ausgewählt.');
        }

        if (!this.threadId) {
          await this.newChat(agentId);
        }
        if (requestGeneration !== this.chatRequestGeneration) {
          const error = new Error('Die Chat-Anfrage wurde abgebrochen.');
          error.name = 'AbortError';
          throw error;
        }

        const responseStream = this.chatStreamingService.streamAgentChat({
          endpointChat: this.endpointChat,
          endpointAgent: this.endpointAgent,
          threadId: this.threadId,
          agentId,
          messages: this.messages,
          conversationSummary: this.activeChatSummary,
          webAccessEnabled,
          modelMode: this.chatModelMode,
          onThreadId: (threadId) => {
            this.threadId = threadId;
          }
        });
        await this.consumeChatStream(responseStream);
      } catch (error) {
        if (this.isAbortError(error)) {
          runInAction(() => {
            this.markLastAssistantMessageCanceled();
            this.chatStatus = 'Antwort abgebrochen.';
          });
        } else {
          runInAction(() => {
            this.removeFailedChatRequest(userMessage);
            this.chatStatus = '';
          });
          throw error;
        }
      }

      runInAction(() => {
        this.chatStatus = '';
      });
      await this.autoSaveCurrentChatSafely();
    } finally {
      runInAction(() => {
        this.chatRequestInFlight = false;
      });
    }
  }

  cancelChat() {
    this.cancelActiveChatRequest();
  }

  clearCurrentChat() {
    this.cancelActiveChatRequest();
    runInAction(() => {
      this.messages = [];
      this.chatStatus = '';
      this.threadId = '';
      this.activeChatId = undefined;
      this.activeChatName = 'Neuer Chat';
      this.activeChatSummary = '';
      this.chatMeta = { prompt: '' };
    });
  }

  async warmUpChat(
    agentId?: string,
    wait = false,
    threadId = this.threadId
  ): Promise<void> {
    await this.chatStreamingService.warmUp({
      endpointChat: this.endpointChat,
      threadId,
      agentId: this.resolveChatAgentId(agentId),
      modelMode: this.chatModelMode,
      wait
    });
  }

  async enhancePrompt(prompt: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointChat}/enhance-prompt`,
      method: 'POST',
      body: JSON.stringify({
        prompt,
        model_mode: this.chatModelMode,
        web_access_enabled: this.webAccessEnabled,
        agent_id: this.resolveChatAgentId()
      })
    });

    if (
      response &&
      typeof response === 'object' &&
      typeof response['prompt'] === 'string'
    ) {
      return response['prompt'];
    }

    return prompt;
  }

  async saveChat() {
    const payload: {
      messages: ChatMessage[];
      conversation_summary?: string;
    } = {
      messages: this.messages,
      conversation_summary: this.activeChatSummary
    };

    if (!this.threadId) {
      await this.newChat();
    }

    const chatIdentifier =
      this.normalizePersistedChatId(this.activeChatId) || this.threadId;
    const response = await this.api.fetch({
      endpoint: `${this.endpointChat}/save/${encodeURIComponent(
        chatIdentifier
      )}`,
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (response && typeof response === 'object') {
      runInAction(() => {
        const savedId = response['id'];
        const savedName = response['name'] || response['title'];

        if (savedId != null) {
          this.activeChatId = String(savedId);
        }
        if (typeof savedName === 'string' && savedName.trim().length > 0) {
          this.activeChatName = savedName;
        }
        if (typeof response['summary'] === 'string') {
          this.activeChatSummary = response['summary'];
        }
      });
    }

    return response;
  }

  async deleteChat(id: string) {
    const normalizedId = this.normalizePersistedChatId(id);
    if (normalizedId && String(this.activeChatId || '') === normalizedId) {
      runInAction(() => {
        this.activeChatId = undefined;
      });
    }

    const response = await this.api.fetch({
      endpoint: `${this.endpointChat + '/delete/' + id}`,
      method: 'DELETE',
      body: JSON.stringify({})
    });
    if (this.hasNoResponse(response)) {
      return;
    }
  }

  async newChat(agentId?: string) {
    const resolvedAgentId = this.resolveChatAgentId(agentId);
    const response = await this.api.fetch({
      endpoint: `${this.endpointChat + '/new'}`,
      method: 'GET',
      queryParams: {
        agent_id: resolvedAgentId,
        model_mode: this.chatModelMode
      }
    });
    if (typeof response !== 'string' || response.length === 0) {
      return;
    }
    runInAction(() => {
      this.threadId = response;
      this.activeChatSummary = '';
    });
  }

  async loadChat(id: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointChat + '/load/' + id}`,
      method: 'GET'
    });

    if (!response || typeof response !== 'object') {
      return;
    }

    const chatEntries = Array.isArray(response['chat']) ? response['chat'] : [];
    const threadId =
      typeof response['thread_id'] === 'string' ? response['thread_id'] : '';
    const summary =
      typeof response['summary'] === 'string' ? response['summary'] : '';
    const chatId =
      this.normalizePersistedChatId(response['id']) ||
      this.normalizePersistedChatId(id);

    runInAction(() => {
      this.messages = chatEntries;
      this.threadId = threadId;
      this.activeChatId = chatId;
      this.activeChatSummary = summary;
    });
    void this.warmUpChat(undefined, false, threadId);
  }

  async loadChatList() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointChat + '/show'}`,
      method: 'GET'
    });

    if (!Array.isArray(response)) {
      return;
    }

    runInAction(() => {
      const seenChatIds = new Set<string>();
      this.chats = response
        .map((chat) => ({
          ...chat,
          id: String(chat?.id ?? '')
        }))
        .filter((chat) => {
          if (!chat.id || seenChatIds.has(chat.id)) {
            return false;
          }

          seenChatIds.add(chat.id);
          return true;
        });
    });
  }

  async editChatTitleChat(id: string, name: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointChat + '/rename/' + id}`,
      method: 'POST',
      body: JSON.stringify({ new_name: name })
    });
    runInAction(() => {
      this.passwordChangeResult = response;
    });
  }

  private removeFailedChatRequest(userMessage: ChatMessage) {
    const assistantIndex = this.messages.length - 1;
    const userIndex = assistantIndex - 1;

    if (
      this.messages[assistantIndex]?.role === 'assistant' &&
      this.messages[userIndex]?.role === 'user' &&
      this.messages[userIndex]?.content === userMessage.content &&
      this.messages[userIndex]?.thread_id === userMessage.thread_id
    ) {
      this.messages.splice(userIndex, 2);
    }
  }

  async autoSaveCurrentChat(refreshList = true) {
    if (this.messages.length === 0) {
      return undefined;
    }

    if (this.chatAutoSavePromise) {
      this.chatAutoSavePending = true;
      return this.chatAutoSavePromise;
    }

    let savedChat: unknown = undefined;

    do {
      this.chatAutoSavePending = false;
      this.chatAutoSavePromise = (async () => {
        const response = await this.saveChat();
        if (refreshList) {
          await this.loadChatList();
        }
        return response;
      })();

      try {
        savedChat = await this.chatAutoSavePromise;
      } finally {
        this.chatAutoSavePromise = undefined;
      }
    } while (this.chatAutoSavePending);

    return savedChat;
  }

  private isAbortError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      String((error as { name?: string }).name) === 'AbortError'
    );
  }

  private async consumeChatStream(
    responseStream: AsyncGenerator<ChatStreamChunk>
  ) {
    await this.chatStreamingService.consume(responseStream, {
      appendToLastAssistant: (content, sources) => {
        runInAction(() => {
          const lastIndex = this.messages.length - 1;
          this.messages[lastIndex].content += content;

          if (sources) {
            this.messages[lastIndex].sources = sources;
          }
        });
      },
      replaceLastAssistant: (content, sources) => {
        runInAction(() => {
          const lastIndex = this.messages.length - 1;
          this.messages[lastIndex].content = content;

          if (sources) {
            this.messages[lastIndex].sources = sources;
          }
        });
      },
      setStatus: (status) => {
        runInAction(() => {
          this.chatStatus = status;
        });
      }
    });
  }

  private async autoSaveCurrentChatSafely() {
    try {
      return await this.autoSaveCurrentChat();
    } catch (error) {
      console.warn(
        'Chat konnte nach der Antwort nicht gespeichert werden.',
        error
      );
      return undefined;
    }
  }

  private markLastAssistantMessageCanceled() {
    const lastIndex = this.messages.length - 1;
    const lastMessage = this.messages[lastIndex];

    if (lastMessage?.role === 'assistant' && !lastMessage.content.trim()) {
      lastMessage.content = 'Antwort abgebrochen.';
    }
  }

  private resolveChatAgentId(agentId?: string): string {
    const candidate = agentId || this.activeAgentId || 'x1F';
    const normalized = String(candidate || '').trim();

    if (
      !normalized ||
      ['undefined', 'null', 'none'].includes(normalized.toLowerCase())
    ) {
      return 'x1F';
    }

    return normalized;
  }

  private normalizePersistedChatId(id: unknown): string | undefined {
    const normalized = String(id ?? '').trim();

    if (
      !normalized ||
      ['undefined', 'null', 'none'].includes(normalized.toLowerCase())
    ) {
      return undefined;
    }

    if (normalized.startsWith('local-')) {
      return undefined;
    }

    return normalized;
  }
}
